import { memo, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getGridViewportPreviewStyle } from '../utils/gridViewport';

const GridViewportPreview = memo(function GridViewportPreview({
  viewportStore,
  interactive = false,
  alwaysVisible = false,
  onMove,
  boardRef,
}) {
  const viewport = useSyncExternalStore(
    viewportStore.subscribe,
    viewportStore.getSnapshot,
    viewportStore.getSnapshot
  );
  const [isVisible, setIsVisible] = useState(alwaysVisible);
  const dragRef = useRef(null);
  const previousPositionRef = useRef(null);
  const hideTimerRef = useRef(null);
  const previewStyle = getGridViewportPreviewStyle(viewport);

  useEffect(() => {
    const position = viewport
      ? [viewport.width, viewport.height, viewport.scrollLeft, viewport.scrollTop,
        viewport.contentWidth, viewport.contentHeight].join(':')
      : null;
    if (previousPositionRef.current === null) {
      previousPositionRef.current = position;
      if (alwaysVisible) setIsVisible(true);
      else if (position) {
        setIsVisible(true);
        hideTimerRef.current = setTimeout(() => setIsVisible(false), 1500);
      }
      return;
    }
    if (previousPositionRef.current === position) return;
    previousPositionRef.current = position;
    if (alwaysVisible) return;

    setIsVisible(Boolean(position));
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (position) {
      hideTimerRef.current = setTimeout(() => setIsVisible(false), 1500);
    }
  }, [alwaysVisible, viewport]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  if (!previewStyle) return null;

  const beginDrag = (event) => {
    if (!interactive || !onMove) return;
    event.preventDefault();
    event.stopPropagation();
    const board = boardRef?.current;
    if (!board) return;
    const rect = board.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: Number(viewport.scrollLeft) || 0,
      startTop: Number(viewport.scrollTop) || 0,
      maxScrollX: previewStyle.maxLeft,
      maxScrollY: previewStyle.maxTop,
      maxMoveX: rect.width * (1 - previewStyle.widthPct / 100),
      maxMoveY: rect.height * (1 - previewStyle.heightPct / 100),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!interactive || !drag || drag.pointerId !== event.pointerId || !onMove) return;
    event.preventDefault();
    const left = Math.max(0, Math.min(drag.maxScrollX,
      drag.startLeft + ((event.clientX - drag.startX) / (drag.maxMoveX || 1)) * drag.maxScrollX));
    const top = Math.max(0, Math.min(drag.maxScrollY,
      drag.startTop + ((event.clientY - drag.startY) / (drag.maxMoveY || 1)) * drag.maxScrollY));
    onMove(Math.round(left), Math.round(top));
  };

  const endDrag = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (!alwaysVisible) {
      hideTimerRef.current = setTimeout(() => setIsVisible(false), 1500);
    }
  };

  return (
    <div
      className={`display_viewport_preview${interactive ? ' display_viewport_preview_interactive' : ''}${isVisible || alwaysVisible ? ' display_viewport_preview_visible' : ''}`}
      style={{
        left: previewStyle.left,
        top: previewStyle.top,
        width: previewStyle.width,
        height: previewStyle.height,
        borderWidth: '2px',
      }}
      aria-hidden='true'
      onPointerDown={interactive ? beginDrag : undefined}
      onPointerMove={interactive ? moveDrag : undefined}
      onPointerUp={interactive ? endDrag : undefined}
      onPointerCancel={interactive ? endDrag : undefined}
    >
      <span className='display_viewport_preview_edge display_viewport_preview_top' />
      <span className='display_viewport_preview_edge display_viewport_preview_right' />
      <span className='display_viewport_preview_edge display_viewport_preview_bottom' />
      <span className='display_viewport_preview_edge display_viewport_preview_left' />
    </div>
  );
});

export default GridViewportPreview;